import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatMenuTrigger } from "@angular/material/menu";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { checklistItemCommands } from "../cbta-web/checklist/checklist-navigation";
import { formatAppVersion } from "../core/app-version";
import { AppUpdateService } from "../core/pwa/app-update.service";
import { Roles, RouteType } from "../models/enum";
import { CandidateChecklistItem, User } from "../models/models";
import { HttpService } from "../shared/services/http.service";
import { LoaderService } from "../shared/services/loader.service";
import { ProfileService } from "../shared/services/profile.service";
import { RoleService } from "../shared/services/role.service";
import { SharedService } from "../shared/services/shared.service";
import { warmAvatarCache } from "../shared/utils/avatar-cache.utils";
import { browserNavigation } from "../shared/utils/browser-navigation.utils";

const ACTION_CENTER_ROLES: Roles[] = [Roles.INSTRUCTOR, Roles.CABIN_CREW_INSTRUCTOR, Roles.PILOT, Roles.CABIN_CREW];

const ROLE_LABELS: Record<Roles, string> = {
	[Roles.ADMIN]: "Admin",
	[Roles.INSTRUCTOR]: "Instructor",
	[Roles.PILOT]: "Pilot",
	[Roles.OFFICE_ADMIN]: "Office Admin",
	[Roles.GROUP_ADMIN]: "Group Admin",
	[Roles.CABIN_CREW]: "Cabin Crew",
	[Roles.CABIN_CREW_INSTRUCTOR]: "Cabin Crew Instructor",
};

@Component({
	selector: "app-header",
	templateUrl: "./header.component.html",
	styleUrls: ["./header.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
	user?: User;
	isOnline: boolean = true;
	enableProfileMenu: boolean = true;
	notificationItems: CandidateChecklistItem[] = [];
	notificationTotal = 0;
	readonly appVersion = formatAppVersion();
	private isCheckingForUpdates = false;
	private profilePreviewSub?: Subscription;

	@ViewChild("profileMenuTrigger") trigger!: MatMenuTrigger;

	constructor(
		private sharedService: SharedService,
		private roleService: RoleService,
		private profileSevice: ProfileService,
		private cdr: ChangeDetectorRef,
		private router: Router,
		private appUpdateService: AppUpdateService,
		private loadingService: LoaderService,
		private httpService: HttpService,
	) {}

	ngOnInit(): void {
		// Assign before any BehaviorSubject subscriptions that call detectChanges().
		this.user = this.roleService.userDetails;
		if (this.user?.picture) {
			void warmAvatarCache(this.user.picture);
		}
		this.profilePreviewSub = this.profileSevice.profilePreview$?.subscribe(() => {
			this.cdr.detectChanges();
		});

		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			console.log("header ngOnInit : ", status);
			this.isOnline = status;
			if (status) {
				this.loadActionCenterPreview();
			} else {
				this.notificationItems = [];
				this.notificationTotal = 0;
			}
			this.cdr.detectChanges();

			if (!this.isOnline) {
				this.enableProfileMenu = false;
				if (this.roleService.hasInstructorView) {
					this.router.navigate([RouteType.PENDING]);
				}
			} else {
				this.enableProfileMenu = true;
			}
		});
		if (navigator.onLine) {
			this.isOnline = true;
			this.profileSevice.initUser(false);
			this.loadActionCenterPreview();
		} else {
			this.isOnline = false;
		}
	}

	ngOnDestroy(): void {
		this.profilePreviewSub?.unsubscribe();
	}

	openMenu(): void {
		if (!this.isOnline) {
			this.trigger.closeMenu();
			return;
		}
		this.trigger.openMenu();
	}

	signout() {
		this.sharedService.logout();
	}

	goToProfile(): void {
		window.location.href = RouteType.PROFILE;
	}

	switchRole() {
		this.sharedService.switchRoles();
	}

	async checkForUpdates(): Promise<void> {
		// Prevent multiple simultaneous checks
		if (this.isCheckingForUpdates) {
			return;
		}

		this.isCheckingForUpdates = true;
		this.loadingService.setLoading(true);

		try {
			// Use setTimeout to yield to the UI thread before starting the check
			await new Promise((resolve) => setTimeout(resolve, 0));
			await this.appUpdateService.manualCheck();
		} catch (error) {
			console.error("Error checking for updates:", error);
		} finally {
			this.isCheckingForUpdates = false;
			this.loadingService.setLoading(false);
		}
	}

	get showSwitchRole(): boolean {
		return this.roleService.isMultiRole;
	}

	get showActionCenterBell(): boolean {
		if (!this.isOnline) {
			return false;
		}
		const roles = this.roleService.userDetails?.roles ?? [];
		return roles.some((role) => ACTION_CENTER_ROLES.includes(role as Roles));
	}

	loadActionCenterPreview(): void {
		if (!this.showActionCenterBell) {
			this.notificationItems = [];
			this.notificationTotal = 0;
			return;
		}

		this.httpService.getCandidateChecklistPreview().subscribe({
			next: (resp) => {
				const payload = resp?.data ?? resp ?? {};
				const items = Array.isArray(payload?.items) ? payload.items : [];
				this.notificationItems = items.slice(0, 5);
				this.notificationTotal = Number(payload?.total ?? items.length) || 0;
				this.cdr.detectChanges();
			},
			error: () => {
				this.notificationItems = [];
				this.notificationTotal = 0;
				this.cdr.detectChanges();
			},
		});
	}

	openActionCenterItem(item: CandidateChecklistItem): void {
		const commands = checklistItemCommands(item);
		if (!commands || !item.role) {
			return;
		}
		this.roleService.autoSwitchRole(item.role);
		this.roleService.setRolesBool();
		browserNavigation.assignWindowLocation(commands.join("/"));
	}

	notificationTitle(item: CandidateChecklistItem): string {
		return item.formName || item.templateName || "Request";
	}

	notificationTypeLabel(item: CandidateChecklistItem): string {
		if (item.taskType === "standalone_feedback" || item.taskType === "feedback") {
			return "Feedback";
		}
		return "Sign Off";
	}

	roleLabel(role?: string): string {
		if (!role) {
			return "";
		}
		return ROLE_LABELS[role as Roles] ?? role;
	}

	formatNotificationTime(submittedAt?: string): string {
		if (!submittedAt) {
			return "";
		}
		const date = new Date(submittedAt);
		if (Number.isNaN(date.getTime())) {
			return "";
		}
		return date.toLocaleString(undefined, {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	get routeType() {
		return RouteType;
	}

	get profilePreview(): string | undefined {
		return this.profileSevice.profilePreview;
	}

	get currentRoleLabel(): string {
		return this.roleLabel(this.roleService.getUserRole());
	}
}
